import React from 'react';
import { useDebouncedCallback } from '@tanstack/react-pacer';

import { TrackableList } from 'types/trackables';
import { useTrackableLists, UseTrackableListsParams } from 'trackables/hooks';

import { Select, SelectProps } from 'components/Select/Select';

export type TrackableListSelectProps<IsMulti extends boolean = false> =
  SelectProps<TrackableList, IsMulti> & {
    queryFilters?: UseTrackableListsParams;
  };

export function TrackableListSelect<IsMulti extends boolean = false>({
  placeholder = 'Watchlists',
  isLoading,
  queryFilters = {},
  ...props
}: TrackableListSelectProps<IsMulti>) {
  const [filters, setFilters] = React.useState<UseTrackableListsParams>({});

  const { data, isLoading: isLoadingOptions } = useTrackableLists({
    params: { ...queryFilters, ...filters },
    keepPreviousData: true,
  });

  const filteredOptions = React.useMemo(() => {
    const options = data?.data;
    const objectTypes = queryFilters.object_types;

    if (!options || !objectTypes || objectTypes.length === 0) {
      return options;
    }

    const allowed = new Set(objectTypes);
    return options.filter((option) => allowed.has(option.object_type));
  }, [data?.data, queryFilters.object_types]);

  const handleInputChange = useDebouncedCallback(
    (value: string) => {
      setFilters((previousFilters) => ({
        ...previousFilters,
        trackable_list_names: value ? [value] : undefined,
      }));
    },
    { wait: 100 }
  );

  return (
    <Select
      {...props}
      options={filteredOptions}
      totalElements={data?.total_elements}
      onInputChange={handleInputChange}
      getOptionLabel={({ name }) => name}
      getOptionValue={({ id }) => id}
      isLoading={isLoading || isLoadingOptions}
      placeholder={placeholder}
    />
  );
}
